// Project → Presentation tab. Hits the existing /projects/:id/presentations
// endpoint (routes/presentations.ts line 218-226) which returns the
// project-scoped presentation list — same data the cross-project
// PresentationsListPage uses but pre-filtered server-side.
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useOutletContext } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { timeAgo } from "@/lib/utils";
import {
  useCreatePresentation,
  createPresentationErrorMessage,
} from "@/lib/useCreatePresentation";
import type { ProjectDetailResponse } from "@/pages/ProjectDetailPage";

interface PresentationRow {
  id: string;
  presentationNumber: number;
  introduction: string | null;
  status: string;
  layout: string | null;
  hidePricing: boolean;
  itemCount: number;
  sentAt: string | null;
  lastActivityAt: string | null;
  updatedAt: string | null;
  createdAt: string | null;
}

interface PresentationsListResponse {
  rows: PresentationRow[];
}

export function ProjectPresentationTab() {
  const data = useOutletContext<ProjectDetailResponse>();
  const projectId = data.project.id;

  const { data: list, isLoading } = useQuery({
    queryKey: ["project", projectId, "presentations"],
    queryFn: () =>
      api<PresentationsListResponse>(`/projects/${projectId}/presentations`),
  });

  // In-context create — SAME mutation the Actions dropdown uses (POST
  // /presentations with project_id, then navigate to the in-project builder).
  // The old dead-end here was a Link to /clients/:id (the Accounts page), which
  // stranded reps who arrived from the stage rail's "New presentation" chip.
  const createPresentation = useCreatePresentation(projectId);

  // Friendly inline failure copy (no raw status codes), derived from the shared
  // create mutation's error state via the ClientProjectsTab-shaped helper.
  const createError = createPresentationErrorMessage(createPresentation.error);

  const rows = list?.rows ?? [];

  // Auto-open the single presentation (2026-07-24 CEO UX fix). With
  // exactly one presentation, the tab should not show a one-card list —
  // it should immediately show that presentation inline, matching the
  // estimate/SO mental model.
  //
  // <Navigate replace> (not push) keeps browser Back sane: it REPLACES
  // the bare tab URL (/projects/:id/presentation) in history with the
  // detail URL rather than stacking a new entry. So hitting Back from
  // the presentation returns to whatever tab the rep was on before,
  // instead of bouncing back onto /presentation which would just
  // auto-redirect forward again (a Back-button trap). A push-Navigate
  // would create exactly that loop; replace avoids it.
  //
  // This branch only fires on the exact tab route; the detail route
  // (/presentation/:presentationId) renders PresentationDetailPage, so
  // there's no redirect once a presentation is open.
  const onlyPresentation = rows.length === 1 ? rows[0] : undefined;
  if (onlyPresentation) {
    return (
      <Navigate
        to={`/projects/${projectId}/presentation/${onlyPresentation.id}`}
        replace
      />
    );
  }

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
          Presentations
        </h2>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => createPresentation.mutate()}
          disabled={createPresentation.isPending}
        >
          <Plus className="h-4 w-4" />
          {createPresentation.isPending ? "Creating…" : "New presentation"}
        </Button>
      </div>

      {createError && (
        <p role="alert" className="mb-4 text-sm text-rust-600">{createError}</p>
      )}

      {isLoading ? (
        <div className="rounded-md border border-rule bg-white p-10 text-center text-sm text-muted">
          Loading presentations…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-rule bg-white p-10 text-center text-sm text-muted">
          No presentations on this project yet. Click{" "}
          <strong>New presentation</strong> to start one.
        </div>
      ) : (
        <ul className="overflow-hidden rounded-md border border-rule bg-white">
          {rows.map((p) => (
            <li key={p.id} className="[&:last-child>a]:border-0">
              <Link
                to={`/projects/${projectId}/presentation/${p.id}`}
                className="group flex items-center gap-4 border-b border-[#e7e7e4] px-[18px] py-[15px] transition-colors hover:bg-[#fafaf8]"
              >
                <div className="flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="font-medium text-ink">
                      P-{p.presentationNumber}
                    </span>
                    <StatusPill tone={statusTone(p.status)}>
                      {statusLabel(p.status)}
                    </StatusPill>
                    <span className="text-xs text-muted">
                      {p.itemCount} item{p.itemCount === 1 ? "" : "s"}
                    </span>
                  </div>
                  {p.introduction && (
                    <div className="mt-1 line-clamp-1 text-sm text-muted">
                      {p.introduction}
                    </div>
                  )}
                  <div className="mt-1 text-xs text-muted">
                    Last activity {timeAgo(p.lastActivityAt ?? p.updatedAt)}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function statusLabel(s: string): string {
  return s === "comments_pending" ? "Client notes" : s.charAt(0).toUpperCase() + s.slice(1);
}

function statusTone(s: string): "active" | "prospect" | "dormant" | "closed" | "info" {
  switch (s) {
    case "draft":
      return "dormant";
    case "sent":
    case "viewed":
      return "prospect";
    case "approved":
      return "active";
    case "comments_pending":
      return "dormant";
    case "rejected":
      return "closed";
    default:
      return "info";
  }
}
